// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — pierQuests.js   ·   THE REPRESENTATIVES
// ────────────────────────────────────────────────────────────────────
// caP'n, this session:
//
//     "go play another game gain currency, come back and trade it for
//      UP Dawgs, but yeah specific challenges would be gorgeous and
//      make the game feel so alive, maybe a representative at the
//      entrance of every game?? they can have quests to do etc"
//
// So every portal on the boardwalk has somebody standing at it with
// work. You take a job, you walk through the door, you come back, you
// get paid in ✹ Dawgs.
//
// ── ⭐ HOW THIS STAYS LEGAL — and it is the whole design ────────────
//
// THE ECONOMY LAW (PIERRE_PRIME L4): **every title writes only its own
// currency. Reads are universal.** Terror writes Chocolate Coins. Snake
// Eyes writes Snake Eyes Coins. Pierre may not reach into either.
//
// A quest that required Terror to pay you Dawgs would break that law and
// would need a code change in somebody else's repo. So no quest does.
// Instead:
//
//   **PIERRE PAYS. PIERRE ONLY EVER READS THE OTHER GAME.**
//
// The representative gives you a job, you go do it in their game, and
// the ONLY thing Pierre does on your return is READ something that game
// already writes for its own reasons — an achievement, or a currency
// balance — and then pay you out of its own pocket in its own currency.
//
// ⚑ Not one line of Terror, Snake Eyes or any other title changes. No
// partner handoff, no new shared file, no schema to agree on. That is
// why this can ship today, and it is the direct answer to OW-15
// ("Pierre sending players out to the other games and paying on
// return"), built on the one currency that is safe to pay repeatedly.
//
// ── AND WHY IT IS DAWGS AND NOT ◆ ───────────────────────────────────
// THE FAUCET LAW (L8): Divine Crests come from ONE-TIME sources only —
// achievements, boss first-kills, secrets. Never anything repeatable.
// A repeatable quest paying DC would be exactly the illegal shape. A
// repeatable quest paying Dawgs is fine, because Dawgs are acquire-only
// (see upDawgs.js) and can never become DC. ⚑ The one-way door is what
// makes this whole feature possible.
//
// ── FIREBASE ────────────────────────────────────────────────────────
//   games/pierre/quests/{uid}/{questId} = { status, acceptedAt, snapshot, doneAt }
// One node, owner-write, inside `games/pierre` which is already in the
// rules tree. Nothing else is written anywhere by this file except the
// Dawgs payout, which goes through grantDawgs like every other faucet.
// ════════════════════════════════════════════════════════════════════

import { ref, onValue, get, update, remove, runTransaction } from "firebase/database";
import { db, auth } from "./firebase";
import { PIERRE_BASE, KNOWN_CURRENCIES, balancePath, currencyMeta, getRates } from "./ieEconomy";
import { GAME_NAMESPACES, ACHIEVEMENTS, achievementsForGame, ACHIEVEMENT_RARITY_META } from "./achievements";
import { grantDawgs } from "./upDawgs";

export const QUESTS_PATH = `${PIERRE_BASE}/quests`;

// ════════════════════════════════════════════════════════════════════
// ⭐ THE REGISTRY SEAM — one row adds a game's representative
// ────────────────────────────────────────────────────────────────────
// Keyed by the achievement namespace / portal id, NOT by the currency's
// `owningApp` — those two use different slugs for the same game
// ("casino" here vs "snake-eyes" in the rate row, "sol" vs
// "sol-survivor"), and that mismatch is exactly the kind of thing that
// silently produces an empty quest board. It is written out once, here,
// and nowhere else has to know.
//
// Adding a title's rep is ONE ROW. No component changes, no new file.
// ════════════════════════════════════════════════════════════════════
export const REPS = {
  terror:       { name: "Ledgerman Osk",   currency: "CC", line: "The Shatterfield keeps its own books. I keep mine." },
  casino:       { name: "Chip Vashti",     currency: "SE", line: "The house remembers. So do I, and I pay better." },
  sol:          { name: "Warden Ilse",     currency: "SOL", line: "Out there it is bright and it is long. Come back and I will make it worth it." },
  perry:        { name: "Sparring Yun",    currency: "KD", line: "Get hit less. Bring me the tally." },
  annihilation: { name: "Quartermaster Vo", currency: "BM", line: "There is a war on. There is also paperwork." },
  bebecitos:    { name: "Nursemaid Pell",  currency: "DW", line: "Mind the little ones and I will mind your purse." },
  swat:         { name: "Crumb-Counter Ib", currency: "SW", line: "Every crumb is counted. That is the job." },
};

// A rep for a game with no row yet — the board is never empty and never
// crashes on an unregistered portal.
export const genericRep = (gameId) => ({
  name: "The Representative",
  currency: null,
  line: `Somebody from ${GAME_NAMESPACES[gameId]?.label || "over there"} is supposed to be here.`,
});

export const repFor = (gameId) => REPS[gameId] || genericRep(gameId);

// ════════════════════════════════════════════════════════════════════
// THE DECK
// ────────────────────────────────────────────────────────────────────
// Two shapes, both verified by a READ and nothing else:
//
//   "earn"        — go and earn N of that game's own coin. On accept we
//                   snapshot your balance; on return we check the delta.
//                   Repeatable.
//   "achievement" — come back when that game has marked you with a
//                   specific achievement. One-time, because an
//                   achievement is.
//
// ⚠ THE ONE HOLE IN "earn", NAMED SO NOBODY HAS TO FIND IT: you could
// buy that game's coin at the Crest Well instead of earning it, and the
// delta would not know the difference. It is left open ON PURPOSE.
// Closing it would mean Pierre tracking every Well movement per
// currency, and the exploit costs the player a Divine Crest — endgame
// money — to obtain some Dawgs, which are bread. Nobody trades down
// like that, and if they want to, they have paid a fair price for the
// privilege.
// ════════════════════════════════════════════════════════════════════

// Reward scale. A quest is worth roughly a barn or two of a crop bed.
const EARN_REWARD = 900;
const RARITY_REWARD = {
  common: 700, uncommon: 1200, rare: 2200, epic: 4000,
  legendary: 7000, mythic: 12000, mythical: 12000,
};
const rewardForRarity = (r) => RARITY_REWARD[String(r || "").toLowerCase()] || 1200;

// How much of their coin one "earn" job asks for. Derived from the
// currency's own rate row so the ask is the same TOIL in every game
// rather than the same NUMBER — a thousand Chocolate Coins and a
// thousand Sol are not the same afternoon.
const earnTargetFor = (currencyId, rates) => {
  const perCrest = Number(currencyMeta(currencyId, rates)?.perCrest) || 1000;
  return Math.max(1, Math.round(perCrest * 0.5));   // half a Crest's worth
};

// ⚑ BUILT FROM THE LIVE REGISTRIES, NEVER HAND-LISTED. Achievement ids
// are read out of `achievements.js` at call time, so a renamed or
// retired achievement drops out of the board instead of becoming a
// quest nobody can ever complete.
export function questsForGame(gameId, rates, achievementMap = {}) {
  const rep = repFor(gameId);
  const out = [];

  if (rep.currency && KNOWN_CURRENCIES[rep.currency]) {
    const meta = KNOWN_CURRENCIES[rep.currency];
    const target = earnTargetFor(rep.currency, rates);
    out.push({
      id: `${gameId}__earn`,
      game: gameId,
      kind: "earn",
      currency: rep.currency,
      target,
      rewardUD: EARN_REWARD,
      repeatable: true,
      name: `A Purse of ${meta.name}`,
      ask: `Go and earn ${target.toLocaleString()} ${meta.icon} ${meta.name}. I will know when you have.`,
    });
  }

  // Up to three achievement jobs, cheapest first, deterministic order so
  // the board does not reshuffle itself every time it is opened.
  let picked = 0;
  // ⚠ `achievementsForGame()` RETURNS ROWS, NOT IDS. Verified in
  // achievements.js — `Object.values(ACHIEVEMENTS).filter(...)`. Keying
  // ACHIEVEMENTS by one of those objects yields undefined and the whole
  // board comes back with only the earn job on it, silently. Map to ids.
  const ids = (achievementsForGame?.(gameId) || [])
    .map((a) => a?.id).filter(Boolean).sort();
  for (const id of ids) {
    if (picked >= 3) break;
    const a = ACHIEVEMENTS[id];
    if (!a) continue;
    if (achievementMap[id]) continue;          // already theirs — not a job
    out.push({
      id: `${gameId}__ach__${id}`,
      game: gameId,
      kind: "achievement",
      achievementId: id,
      rewardUD: rewardForRarity(a.rarity),
      repeatable: false,
      name: a.name || id,
      ask: a.desc || a.description || "Do the thing they will mark you for.",
      rarity: a.rarity,
      rarityTint: ACHIEVEMENT_RARITY_META?.[a.rarity]?.tint || null,
    });
    picked++;
  }

  return out;
}

// ════════════════════════════════════════════════════════════════════
// STATE
// ════════════════════════════════════════════════════════════════════
export function listenToQuests(uid, cb) {
  if (!uid) { cb({}); return () => {}; }
  const off = onValue(ref(db, `${QUESTS_PATH}/${uid}`), (snap) => cb(snap.val() || {}));
  return () => off();
}

// ── ACCEPT ──────────────────────────────────────────────────────────
// For an "earn" job the snapshot is the whole mechanism: it is what
// "since you took the job" means. ⚠ It is read at ACCEPT time and never
// again, so a player cannot re-accept to lower the bar — re-accepting
// takes a FRESH snapshot of a HIGHER balance, which makes the job
// harder, not easier. That asymmetry is deliberate.
export async function acceptQuest(uid, quest, rates) {
  if (!uid || auth.currentUser?.uid !== uid) {
    return { ok: false, message: "You have to be signed in as yourself to take work." };
  }
  if (!quest?.id) return { ok: false, message: "No such job." };

  let snapshot = 0;
  if (quest.kind === "earn") {
    const p = balancePath(uid, currencyMeta(quest.currency, rates));
    // ⚠ A PATH WE CANNOT READ IS NOT A ZERO. Treating "cannot read" as
    // "you have none" would hand the player a job they complete
    // instantly the moment the path resolves. Refuse instead.
    if (!p) return { ok: false, message: "That coin has no home yet. Come back when it does." };
    try {
      const snap = await get(ref(db, p));
      snapshot = snap.exists() ? (Number(snap.val()) || 0) : 0;
    } catch {
      return { ok: false, message: "Could not read your purse just now. Try again." };
    }
  }

  try {
    await update(ref(db, `${QUESTS_PATH}/${uid}/${quest.id}`), {
      status: "accepted",
      kind: quest.kind,
      game: quest.game,
      acceptedAt: Date.now(),
      snapshot,
      target: quest.target || null,
      currency: quest.currency || null,
      achievementId: quest.achievementId || null,
      rewardUD: quest.rewardUD,
    });
  } catch { return { ok: false, message: "The paperwork would not take. Try again." }; }

  return { ok: true, quest: quest.id };
}

export async function abandonQuest(uid, questId) {
  if (!uid || auth.currentUser?.uid !== uid) return { ok: false };
  try { await remove(ref(db, `${QUESTS_PATH}/${uid}/${questId}`)); }
  catch { return { ok: false }; }
  return { ok: true };
}

// ── PROGRESS ────────────────────────────────────────────────────────
// Pure. Feed it what you already have; it reads nothing.
export function questProgress(row, { balances = {}, achievementMap = {} } = {}) {
  if (!row) return { have: 0, need: 1, done: false };
  if (row.kind === "achievement") {
    const done = !!achievementMap[row.achievementId];
    return { have: done ? 1 : 0, need: 1, done };
  }
  const now = Number(balances[row.currency]) || 0;
  const have = Math.max(0, now - (Number(row.snapshot) || 0));
  const need = Number(row.target) || 1;
  return { have, need, done: have >= need };
}

// ── ⭐ THE EVIDENCE, READ FRESH ──────────────────────────────────────
// ⚠⚠ `questProgress` is PURE and therefore only ever as honest as what
// it is handed. The board hands it React state, which is correct for
// DRAWING a progress bar and worthless for PAYING one: a caller that
// says a job is finished is describing what it WANTS to be paid for.
// So the payout path reads its own evidence out of the database and
// never accepts a caller's word for it.
// ⛑ THE SHAPE TO REMEMBER: a pure function cannot be a security check.
// Whoever supplies its arguments is the one making the decision.
// Found by adversarial review 2026-09-14.
async function liveEvidence(uid, row) {
  if (row.kind === "achievement") {
    const snap = await get(ref(db, `profiles/${uid}/achievements/${row.achievementId}`));
    return { achievementMap: { [row.achievementId]: snap.exists() } };
  }
  const p = balancePath(uid, currencyMeta(row.currency, await getRates()));
  // ⚠ A path we cannot resolve is NOT a zero, same as at accept time.
  if (!p) throw new Error("no_balance_path");
  const snap = await get(ref(db, p));
  return { balances: { [row.currency]: snap.exists() ? (Number(snap.val()) || 0) : 0 } };
}

// ── TURN IN ─────────────────────────────────────────────────────────
// ⚠ CLAIM FIRST, THEN PAY, THEN ROLL BACK ON FAILURE — the same ladder
// the harvest uses, and for the same reason. The status flag here is not
// a "claimed" marker, it is the only thing standing between two open
// tabs and two payouts for one job. So the flag is flipped first, by a
// write that also RE-VERIFIES the condition, and if the payment then
// fails the flag goes back so the job is still there to hand in.
export async function turnInQuest(uid, questId) {
  if (!uid || auth.currentUser?.uid !== uid) {
    return { ok: false, message: "You have to be signed in as yourself." };
  }

  let row = null;
  try {
    const snap = await get(ref(db, `${QUESTS_PATH}/${uid}/${questId}`));
    row = snap.val();
  } catch { return { ok: false, message: "Could not find that job." }; }
  if (!row) return { ok: false, message: "You are not carrying that job." };
  if (row.status === "done") return { ok: false, message: "Already paid for that one." };

  // ⭐ RE-VERIFIED AGAINST THE DATABASE. See `liveEvidence` above — the
  // caller is not consulted, deliberately, and `ctx` is gone from this
  // signature so nobody can hand one in again.
  let prog;
  try { prog = questProgress(row, await liveEvidence(uid, row)); }
  catch { return { ok: false, message: "Could not check your work just now. Try again." }; }
  if (!prog.done) return { ok: false, message: "Not finished yet." };

  const reward = Math.max(0, Math.floor(Number(row.rewardUD) || 0));

  // ⭐⭐ THE CLAIM **IS** THE TRANSACTION — it is not a flag written next
  // to one. `update()` always succeeds, so two open tabs both read
  // "accepted", both wrote "done", and both got paid for one job. A
  // transaction that aborts on an already-done row is the only thing
  // that makes the claim exclusive. The harvest has always done it this
  // way (`minerium.js` harvest); this is that ladder, honestly.
  const claim = await runTransaction(ref(db, `${QUESTS_PATH}/${uid}/${questId}`), (cur) => {
    if (!cur || cur.status === "done") return;    // abort — somebody got here first
    return { ...cur, status: "done", doneAt: Date.now() };
  }).catch(() => null);
  if (!claim || !claim.committed) {
    return { ok: false, message: "Already paid for that one." };
  }

  const paid = await grantDawgs(uid, reward, `pierre:quest:${row.game}:${row.kind}`);
  if (paid !== reward) {
    // ⚠⚠ A PARTIAL PAYOUT LEAVES THE JOB **CLOSED**. It used to reopen
    // it, which paid the player AND handed the job back at full value —
    // and because an achievement stays earned forever there was nothing
    // limiting the replay. `grantDawgs` chunks at DAWGS_GRANT_CEILING,
    // so any reward above it makes a partial a NORMAL path, not an edge
    // case: a mythic job at ✹12,000 is a five-bite ladder.
    // ⛑ Same shape as the harvest's rollback, and the same lesson: only
    // roll back when NOTHING was paid. Found by adversarial review
    // 2026-09-14.
    if (paid > 0) {
      await update(ref(db, `${QUESTS_PATH}/${uid}/${questId}`), {
        paidUD: paid, shortfallUD: reward - paid,
      }).catch(() => {});
      return { ok: true, paidUD: paid, partial: true,
               message: `Only ✹${paid} of ✹${reward} came through. The rest is owed — it is written down, not lost.` };
    }
    // Nothing landed, so handing the job back costs nobody anything.
    await update(ref(db, `${QUESTS_PATH}/${uid}/${questId}`), { status: "accepted", doneAt: null }).catch(() => {});
    return { ok: false, message: "The Dawgs did not arrive — the job is still yours. Try again." };
  }

  // A repeatable job clears itself so it can be taken again. A one-time
  // one stays as a `done` row, which is what stops it reappearing on
  // the board.
  if (row.kind === "earn") {
    await remove(ref(db, `${QUESTS_PATH}/${uid}/${questId}`)).catch(() => {});
  }

  return { ok: true, paidUD: paid };
}

// The board for one portal: the deck, minus anything already finished
// for good, with any live row attached.
export function boardFor(gameId, { rates, achievementMap, questRows = {} }) {
  const deck = questsForGame(gameId, rates, achievementMap);

  // ⚠⚠ A JOB YOU ARE CARRYING NEVER LEAVES THE BOARD, and this is not a
  // nicety — without it EVERY achievement job in the feature is
  // unpayable. `questsForGame` skips achievements you already hold
  // ("already theirs — not a job"), which is right for OFFERING work and
  // catastrophic for COLLECTING it: the instant you earn the thing you
  // were sent to earn, the card and its COLLECT button vanish and the
  // accepted row sits in Firebase forever. So the deck is unioned with
  // anything the player is actually holding.
  // ⛑ THE SHAPE TO REMEMBER: the list you OFFER from and the list you
  // PAY from are not the same list, and they diverge at exactly the
  // moment the player succeeds. Found by adversarial review 2026-09-14.
  // ⚠ REBUILT FROM THE ROW, not re-picked from the catalogue. The first
  // version called `questsForGame(gameId, rates, {})` and filtered it —
  // but that picker caps at three and returns the first three UNHELD
  // achievements, so the job the player is actually carrying is only in
  // it by coincidence. Accept the 5th-ranked job, earn it, and the card
  // still vanished. The row already holds everything the card needs.
  const seen = new Set(deck.map((q) => q.id));
  const carried = Object.entries(questRows)
    .filter(([id, row]) => row?.status === "accepted" && !seen.has(id) && String(id).startsWith(`${gameId}__`))
    .map(([id, row]) => {
      const a = row.achievementId ? ACHIEVEMENTS[row.achievementId] : null;
      return {
        id, game: gameId, kind: row.kind,
        achievementId: row.achievementId || null,
        currency: row.currency || null,
        target: row.target || null,
        rewardUD: row.rewardUD,
        title: a?.name || row.title || "A job you took",
        blurb: a?.blurb || row.blurb || "",
        rarity: a?.rarity || row.rarity || null,
        rarityTint: ACHIEVEMENT_RARITY_META?.[a?.rarity]?.tint || null,
        repeatable: row.kind === "earn",
      };
    });

  return [...deck, ...carried]
    .filter((q) => !(questRows[q.id]?.status === "done" && !q.repeatable))
    .map((q) => ({ ...q, row: questRows[q.id] || null }));
}

const pierQuestsApi = {
  REPS, repFor, questsForGame, boardFor,
  listenToQuests, acceptQuest, abandonQuest, turnInQuest, questProgress,
};

export default pierQuestsApi;
